﻿import { FeedbackDto, FeedbackRating } from '../features/map/commands/sendFeedback/feedbackDto';
import { ContactDetailsInput } from '../features/map/components/contactDetails';

const baseUrl = (window as any).apiBaseUrl || "https://localhost:7132";

export interface GetLayerCapabilitiesResponse {
    layerName: string;
    tileMatrixSetLink: string;
    resolutions: number[];
    matrixIds: string[];
}

interface GetLayerCapabilitiesRequest {
    layerName: string;
    projection: string;
}

interface GetStartMapConfigResponse {
    layerName: string;
    center: number[];
    zoom: number;
    projection: string;
}

interface Point {
    x: number;
    y: number;
    epsgCode: number;
}

export interface GetAllPointsResponse {
    points: Point[];
}

interface ApiErrorResponse {
    statusCode: number;
    message: string;
    errors: { [key: string]: string[] };
}

// Fetches the start map configuration from the Config API
async function getStartMapConfig(): Promise<GetStartMapConfigResponse> {
    const url = `${baseUrl}/api/configuration/getstartmapConfig`;

    try {
        const response = await fetch(url, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json'
            }
        });

        if (!response.ok) {
            const errorData: ApiErrorResponse = await response.json();
            throw errorData;
        }

        return await response.json();

    } catch (error) {
        console.error("Failed to fetch start map configuration:", error);
        throw error;
    }
}

export async function getAllPoints(): Promise<GetAllPointsResponse> {
    const url = `${baseUrl}/api/points/getallpoints`;

    try {
        const response = await fetch(url, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json'
            }
        });

        if (!response.ok) {
            const errorData: ApiErrorResponse = await response.json();
            throw errorData;
        }

        return await response.json();

    } catch (error) {
        console.error("Failed to fetch all points:", error);
        throw error;
    }
}


// Fetches the layer capabilities from the API
export async function getStartMap(layerName?: string): Promise<GetLayerCapabilitiesResponse> {
    const startMapData = await getStartMapConfig();


    const url = `${baseUrl}/api/capabilities/getlayercapabilities`;
    const requestBody: GetLayerCapabilitiesRequest = {
        layerName: layerName || startMapData.layerName,
        projection: startMapData.projection
    };


    try {
        const response = await fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(requestBody)
        });

        if (!response.ok) {
            const errorData: ApiErrorResponse = await response.json();
            throw errorData;
        }

        const data = await response.json();
        return {
            layerName: data.layerName,
            tileMatrixSetLink: data.tileMatrixSetLink,
            resolutions: data.resolutions,
            matrixIds: data.matrixIds
        };
    } catch (error) {
        console.error("Failed to fetch layer capabilities:", error);
        throw error;
    }
}

export async function sendFeedbackToApi(data: FeedbackDto): Promise<void> {
    const slug = (window as any).dialogSlug;
    const url = slug
        ? `${baseUrl}/api/dialog/${slug}/feedback`
        : `${baseUrl}/api/feedback`;

    const response = await fetch(url, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json'
        },
        body: JSON.stringify({
            ...(slug && { slug }),
            coordinate: data.coordinate,
            epsg: data.epsg,
            rating: data.rating,
            comment: data.comment,
            userHash: data.userHash,
            contact: data.contact ?? null
        })
    });

    if (!response.ok) {
        if (response.status === 403) {
            throw new DialogClosedError();
        }
        const error = await response.text();
        console.error("Feedback API error:", error);
        throw new Error("Kunde inte skicka feedback");
    }
}

export class DialogClosedError extends Error {
    constructor() {
        super('Dialogen tar inte emot svar just nu.');
        this.name = 'DialogClosedError';
    }
}

export async function getAllFeedback(): Promise<FeedbackDto[]> {
    const slug = (window as any).dialogSlug;
    const url = slug
        ? `${baseUrl}/api/dialog/${slug}/feedback`
        : `${baseUrl}/api/feedback`;
    try {
        const response = await fetch(url, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json'
            }
        });
        if (!response.ok) {
            throw await response.json();
        }
        const data = await response.json();
        const feedbacks = slug ? data.feedbacks : data;
        return feedbacks.map((item: any) => ({
            coordinate: item.coordinate,
            epsg: item.epsg,
            rating: (item.rating || 'neutral') as FeedbackRating,
            comment: '',
            save: false,
            userHash: ''
        }));
    } catch (error) {
        console.error('Failed to fetch feedback points:', error);
        throw error;
    }
}

// Dialog-specific API functions
export interface DialogConfig {
    dialogId: number;
    slug: string;
    title: string;
    description?: string;
    startDate?: string | null;
    endDate?: string | null;
    isOpen: boolean;
    isUpcoming: boolean;
    hasEnded: boolean;
    allowRating: boolean;
    showFeedbackToPublic: boolean;
    showInfoBox: boolean;
    infoTitle?: string | null;
    infoText?: string | null;
    collectContactDetails: boolean;
    contactAskName: boolean;
    contactAskEmail: boolean;
    contactAskPhone: boolean;
    privacyNoticeText?: string | null;
    layerName: string;
    centerX: number;
    centerY: number;
    zoom: number;
    projection: string;
}

export interface DialogQuestion {
    questionId: number;
    questionText: string;
    questionType: string;
    options?: string[];
    scaleMin?: number;
    scaleMax?: number;
    scaleMinLabel?: string;
    scaleMaxLabel?: string;
    isRequired: boolean;
    emoji?: string;
}

export interface DialogQuestionsResponse {
    dialogSlug: string;
    dialogTitle: string;
    questions: DialogQuestion[];
}

export async function getDialogConfig(slug: string): Promise<DialogConfig> {
    const url = `${baseUrl}/api/dialog/${slug}`;
    const response = await fetch(url);
    if (!response.ok) {
        throw new Error('Dialog not found');
    }
    return await response.json();
}

export async function getDialogQuestions(slug: string): Promise<DialogQuestionsResponse> {
    const url = `${baseUrl}/api/dialog/${slug}/questions`;
    const response = await fetch(url);
    if (!response.ok) {
        throw new Error('Questions not found');
    }
    return await response.json();
}

export interface SurveyAnswer {
    questionId: number;
    answerText?: string;
    answerValue?: number;
}

export async function submitDialogSurvey(slug: string, userHash: string, answers: SurveyAnswer[], contact?: ContactDetailsInput): Promise<void> {
    const url = `${baseUrl}/api/dialog/${slug}/survey`;
    const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ slug, userHash, answers, contact: contact ?? null })
    });
    if (!response.ok) {
        if (response.status === 403) {
            throw new DialogClosedError();
        }
        throw new Error('Failed to submit survey');
    }
}

export async function submitDialogFeedback(slug: string, data: FeedbackDto): Promise<void> {
    const url = `${baseUrl}/api/dialog/${slug}/feedback`;
    const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
            slug,
            coordinate: data.coordinate,
            epsg: data.epsg,
            rating: data.rating,
            comment: data.comment,
            userHash: data.userHash,
            contact: data.contact ?? null
        })
    });
    if (!response.ok) {
        if (response.status === 403) {
            throw new DialogClosedError();
        }
        throw new Error('Failed to submit feedback');
    }
}